import { config } from '@/config';
import { axios, publicAxios } from '@/axios';
import { safeFilename, newUid } from '@/util';

function normalizePath(p) {
  return String(p || '').replace(/^\/+/, ''); // remove leading /
}

function extractLessonIdFromPath(path) {
  const p = normalizePath(path).replace(/\/+$/, ''); // drop trailing /
  const m = p.match(/^lessons\/([^/]+)(?:\/.*)?$/); // lessons/<id> OR lessons/<id>/whatever
  return m ? m[1] : null;
}

export function uploadFile(path, file, options) {
  const fireAndForget = options && options.fireAndForget;

  // ✅ Route lesson uploads through uploadLesson
  const lessonId = extractLessonIdFromPath(path);
  if (lessonId) {
    const p = uploadLesson(
      { fields: { key: `lessons/${lessonId}` } },
      file,
      options
    );

    // Keep return type compatible with uploadFile: resolve to a URL string
    // Your old postdata flow returned response0.data.downloadUrl.
    // We can reconstruct it (acc/prod bucket differs) so prefer a backend-provided URL if you add it later,
    // but for now: use the existing convention.
    const downloadUrl =
      options?.downloadUrlOverride ||
      `${config.backend.replace(/\/+$/, '')}/s3/lessons/${encodeURIComponent(
        lessonId
      )}`; // fallback

    if (fireAndForget) {
      return new Promise((resolve) => {
        p.then((r) => resolve(r.downloadUrl)).catch((e) =>
          console.error('Background lesson upload failed', e)
        );
      });
    }

    return p.then((r) => r.downloadUrl);
  }

  // ✅ Default behaviour (S3 postdata flow)
  return new Promise((resolve, reject) => {
    try {
      if (!path.endsWith('/')) path += '/';
      axios
        .post(config.backend + '/document/postdata', {
          path: `${path}${safeFilename(file.name)}`,
          mimetype: file.type ? file.type : undefined,
        })
        .then((response0) => {
          const downloadUrl = response0.data.downloadUrl;

          if (fireAndForget) {
            resolve(downloadUrl);
            _uploadFile(response0.data, file, (e) => {
              if (e) console.error('Background upload failed', e);
            });
          } else {
            _uploadFile(response0.data, file, (e) => {
              if (e) return reject(e);
              resolve(downloadUrl);
            });
          }
        })
        .catch((e) => {
          console.error(e);
          reject(e);
        });
    } catch (e) {
      console.error(e);
      reject(e);
    }
  });
}

export function uploadFileInstruction(path, file, options) {
  const fireAndForget = options && options.fireAndForget;

  // ✅ Route lesson uploads through uploadLesson
  const lessonId = extractLessonIdFromPath(path);
  if (lessonId) {
    const p = uploadLesson(
      { fields: { key: `lessons/${lessonId}` } },
      file,
      options
    );

    const downloadUrl =
      options?.downloadUrlOverride ||
      `${config.backend.replace(/\/+$/, '')}/s3/lessons/${encodeURIComponent(
        lessonId
      )}`;

    if (fireAndForget) {
      return new Promise((resolve) => {
        resolve(downloadUrl);
        p.catch((e) => console.error('Background lesson upload failed', e));
      });
    }

    return p.then(() => downloadUrl);
  }

  // ✅ Default behaviour (S3 postdata flow)
  const onProgress = options?.onProgress;

  return new Promise((resolve, reject) => {
    try {
      if (!path.endsWith('/')) path += '/';
      axios
        .post(config.backend + '/document/postdata', {
          path: `${path}${safeFilename(file.name)}`,
          mimetype: file.type ? file.type : undefined,
        })
        .then((response0) => {
          const downloadUrl = response0.data.downloadUrl;

          if (fireAndForget) {
            resolve(downloadUrl);
            _uploadFileInstruction(response0.data, file, onProgress, (e) => {
              if (e) console.error('Background upload failed', e);
            });
          } else {
            _uploadFileInstruction(response0.data, file, onProgress, (e) => {
              if (e) return reject(e);
              resolve(downloadUrl);
            });
          }
        })
        .catch((e) => {
          console.error(e);
          reject(e);
        });
    } catch (e) {
      console.error(e);
      reject(e);
    }
  });
}

export function uploadString(path, content, mimetype, options) {
  if (!mimetype) mimetype = 'text/plain';

  const normalizedPath = normalizePath(path);
  const parts = normalizedPath.split('/').filter(Boolean);
  const filename = parts.pop() || 'file.txt';
  const directory = parts.length ? `${parts.join('/')}/` : '';

  // ✅ Route lesson uploads through uploadLesson
  const lessonId = extractLessonIdFromPath(normalizedPath);
  if (lessonId) {
    return uploadLesson(
      { fields: { key: `lessons/${lessonId}` } },
      content,
      options
    ).then((r) => r.downloadUrl);
  }

  const file =
    typeof File !== 'undefined'
      ? new File([content], filename, { type: mimetype })
      : (() => {
          const blob = new Blob([content], { type: mimetype });
          blob.name = filename;
          return blob;
        })();

  return uploadFile(directory, file, options);
}

export async function uploadImage(
  file,
  width,
  height,
  format,
  preferredPath,
  options = {}
) {
  var data = new FormData();
  data.append('image', file);
  let path = config.backend + '/s3/upload';
  if (width) {
    path += `/${width}`;
  }
  if (height) {
    path += `/${height}`;
  }
  if (format) {
    path += `/${format}`;
  }
  return new Promise((resolve, reject) => {
    axios
      .post(path, data, {
        headers: {
          'Content-Type': 'multipart/form-data',
          path: preferredPath ? preferredPath : undefined,
          'preserve-animation': options.preserveAnimation ? 'true' : undefined,
          'without-enlargement': options.withoutEnlargement
            ? 'true'
            : undefined,
        },
      })
      .then((response) => {
        resolve(response.data.url);
      })
      .catch((e) => {
        reject(e);
      });
  });
}

function _uploadFile(postData, file, cb, mimetype) {
  let form = new FormData();
  for (let key in postData.fields) {
    form.append(key, postData.fields[key]);
  }
  form.append('file', file);
  publicAxios
    .post(postData.url, form)
    .then((response) => {
      cb(null, response);
    })
    .catch((e) => {
      cb(e);
    });
}

function _uploadFileInstruction(postData, file, onProgress, cb, mimetype) {
  let form = new FormData();
  for (let key in postData.fields) {
    form.append(key, postData.fields[key]);
  }
  form.append('file', file);
  publicAxios
    .post(postData.url, form, {
      onUploadProgress: (event) => {
        if (!onProgress || !event.total) return;
        const percent = Math.round((event.loaded * 100) / event.total);
        onProgress(percent);
      },
    })
    .then((response) => {
      cb(null, response);
    })
    .catch((e) => {
      cb(e);
    });
}

/*
export function uploadLesson(postData, content) {
  return new Promise((resolve, reject) => {
    let blob = new Blob([content], { type: 'text/html' });
    let path_splitted = postData?.fields?.key.split('/');
    blob.filename = path_splitted[path_splitted?.length - 1];
    return _uploadFile(postData, blob, (e, data) => {
      if (e) reject(e);
      else resolve(data);
    });
  });
}
*/

/**
 * Upload sanitized lesson content via backend endpoint:
 *   POST /lessoncontent/:lessonId
 * expects multipart/form-data with field name "file"
 *
 * Returns: { message: "ok", version: string|null }
 */

function extractLessonIdFromPostData(postData) {
  const key = postData?.fields?.key; // e.g. "lessons/19918"
  if (!key || typeof key !== 'string') return null;

  const m = key.match(/^lessons\/(.+)$/);
  if (!m) return null;

  return m[1]; // "19918" (but also supports other ids if you ever change format)
}

/**
 * Upload sanitized lesson content via backend endpoint:
 *   POST /lessoncontent/:lessonId
 * expects multipart/form-data with field name "file"
 *
 * Returns: { message: "ok", version: string|null, key?: string, downloadUrl?: string }
 */
export async function uploadLesson(postData, content, options = {}) {
  const onProgress = options?.onProgress;

  const lessonId = extractLessonIdFromPostData(postData);
  if (!lessonId) {
    throw new Error(
      `uploadLesson: could not extract lessonId from postData.fields.key=${JSON.stringify(
        postData?.fields?.key
      )}`
    );
  }

  const file =
    typeof File !== 'undefined'
      ? new File([content], 'lesson.html', { type: 'text/html' })
      : (() => {
          const blob = new Blob([content], { type: 'text/html' });
          blob.name = 'lesson.html';
          return blob;
        })();

  const form = new FormData();
  form.append('file', file);

  const url = `${config.backend}/lesson/lessoncontent/${encodeURIComponent(
    String(lessonId)
  )}`;

  const res = await axios.post(url, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: (event) => {
      if (!onProgress || !event.total) return;
      const percent = Math.round((event.loaded * 100) / event.total);
      onProgress(percent);
    },
  });

  const downloadUrl =
    res.data?.downloadUrl || postData?.downloadUrl || `lessons/${lessonId}`;

  return { ...res.data, downloadUrl };
}
